/**
 * Jeanerica attribution layer — theme JS
 *
 * Captures UTMs + click IDs on every page load, persists them in a 90-day
 * cookie + localStorage, pushes them onto window.dataLayer for GTM, and
 * writes them to cart `note_attributes` so they survive into the order's
 * `customAttributes` after Shopify's hosted checkout.
 *
 * Lives at: theme/assets/jeanerica-attribution.js
 * Loaded by: theme/snippets/jeanerica-attribution.liquid (rendered before GTM in <head>)
 *
 * Cart attribute keys (all leading underscore so they're hidden from the
 * customer-facing order summary but surface on Order.customAttributes via
 * Admin GraphQL):
 *   _je_first_source, _je_first_medium, _je_first_campaign,
 *   _je_first_landing_url, _je_first_referrer, _je_first_at,
 *   _je_last_source,  _je_last_medium,  _je_last_campaign,
 *   _je_gclid, _je_fbclid, _je_ttclid, _je_msclkid,
 *   _je_session_id, _je_anonymous_id
 */
(function () {
  "use strict";

  var STORAGE_KEY = "je_attribution_v1";
  var COOKIE_NAME = "je_attribution";
  var COOKIE_DAYS = 90;
  var SESSION_IDLE_MIN = 30;
  var SAME_SITE_HOST = (function () {
    try { return location.hostname.replace(/^www\./, ""); } catch (e) { return ""; }
  })();

  // ── helpers ───────────────────────────────────────────────────────
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  function readCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function writeCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; expires=" +
      d.toUTCString() +
      "; path=/; SameSite=Lax" +
      (location.protocol === "https:" ? "; Secure" : "");
  }
  function getStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      var c = readCookie(COOKIE_NAME);
      if (c) return JSON.parse(c);
    } catch (e) { /* ignore */ }
    return null;
  }
  function persist(state) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
    try { writeCookie(COOKIE_NAME, JSON.stringify(state), COOKIE_DAYS); } catch (e) { /* ignore */ }
  }

  // ── parse current page ────────────────────────────────────────────
  function parseCurrent() {
    var p = new URLSearchParams(location.search);
    var get = function (k) { return (p.get(k) || "").trim() || null; };
    var ref = document.referrer || "";
    var refHost = "";
    if (ref) {
      try { refHost = new URL(ref).hostname.replace(/^www\./, ""); } catch (e) {}
    }
    var sameSite = refHost && SAME_SITE_HOST && refHost === SAME_SITE_HOST;
    return {
      source: get("utm_source"),
      medium: get("utm_medium"),
      campaign: get("utm_campaign"),
      content: get("utm_content"),
      term: get("utm_term"),
      gclid: get("gclid"),
      fbclid: get("fbclid"),
      ttclid: get("ttclid"),
      msclkid: get("msclkid"),
      landing_url: location.href,
      referrer: sameSite ? null : ref || null,
    };
  }

  function hasAnyAttribution(t) {
    return !!(t.source || t.medium || t.campaign || t.gclid || t.fbclid || t.ttclid || t.msclkid);
  }

  // ── session ───────────────────────────────────────────────────────
  function rotateSession(state) {
    var now = Date.now();
    var idleMs = SESSION_IDLE_MIN * 60 * 1000;
    if (!state.session_id || !state.session_last_seen || now - state.session_last_seen > idleMs) {
      state.session_id = uuid();
    }
    state.session_last_seen = now;
  }

  function ensureAnonymousId(state) {
    if (state.anonymous_id) return;
    // Prefer PostHog's $device_id so we share identity if PostHog is loaded
    var ph = readCookie("ph_phc_") || readCookie("ph_distinct_id");
    if (ph) {
      try {
        var parsed = JSON.parse(ph);
        if (parsed && parsed.distinct_id) { state.anonymous_id = parsed.distinct_id; return; }
      } catch (e) {}
    }
    state.anonymous_id = uuid();
  }

  // ── main ──────────────────────────────────────────────────────────
  var state = getStored() || {};
  ensureAnonymousId(state);
  rotateSession(state);

  var current = parseCurrent();
  var nowIso = new Date().toISOString();

  if (hasAnyAttribution(current)) {
    if (!state.first_at) {
      state.first_source = current.source;
      state.first_medium = current.medium;
      state.first_campaign = current.campaign;
      state.first_landing_url = current.landing_url;
      state.first_referrer = current.referrer;
      state.first_at = nowIso;
    }
    state.last_source = current.source;
    state.last_medium = current.medium;
    state.last_campaign = current.campaign;
    state.last_landing_url = current.landing_url;
    state.last_referrer = current.referrer;
    state.last_at = nowIso;
    if (current.gclid)  state.gclid  = current.gclid;
    if (current.fbclid) state.fbclid = current.fbclid;
    if (current.ttclid) state.ttclid = current.ttclid;
    if (current.msclkid) state.msclkid = current.msclkid;
  } else if (!state.first_at) {
    // No UTM signal yet, but we still want to record the very first landing
    state.first_source = "direct";
    state.first_medium = "(none)";
    state.first_landing_url = current.landing_url;
    state.first_referrer = current.referrer;
    state.first_at = nowIso;
  }

  persist(state);

  // ── push to dataLayer for GTM (10 variables) ──────────────────────
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    je_first_source: state.first_source || null,
    je_first_medium: state.first_medium || null,
    je_first_campaign: state.first_campaign || null,
    je_last_source: state.last_source || state.first_source || null,
    je_last_medium: state.last_medium || state.first_medium || null,
    je_last_campaign: state.last_campaign || state.first_campaign || null,
    je_gclid: state.gclid || null,
    je_fbclid: state.fbclid || null,
    je_session_id: state.session_id,
    je_anonymous_id: state.anonymous_id,
  });

  // ── write to cart on add/update ───────────────────────────────────
  function buildCartAttributes() {
    return {
      _je_first_source: state.first_source || "",
      _je_first_medium: state.first_medium || "",
      _je_first_campaign: state.first_campaign || "",
      _je_first_landing_url: state.first_landing_url || "",
      _je_first_referrer: state.first_referrer || "",
      _je_first_at: state.first_at || "",
      _je_last_source: state.last_source || "",
      _je_last_medium: state.last_medium || "",
      _je_last_campaign: state.last_campaign || "",
      _je_gclid: state.gclid || "",
      _je_fbclid: state.fbclid || "",
      _je_ttclid: state.ttclid || "",
      _je_msclkid: state.msclkid || "",
      _je_session_id: state.session_id,
      _je_anonymous_id: state.anonymous_id,
    };
  }

  var cartAttrsWritten = false;
  function writeCartAttrs() {
    if (cartAttrsWritten) return;
    cartAttrsWritten = true;
    var body = JSON.stringify({ attributes: buildCartAttributes() });
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
      credentials: "same-origin",
    }).catch(function () { cartAttrsWritten = false; });
  }

  // Hook fetch — Shopify cart endpoints, plus modern cart drawers using Section Rendering API
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : input && input.url;
      var p = origFetch.apply(this, arguments);
      if (url && /\/cart\/(add|change|update)(\.js|\?|$)/.test(url)) {
        p.then(function () { setTimeout(writeCartAttrs, 50); }).catch(function () {});
      }
      return p;
    };
  }
  // Also fire after first user interaction so we don't depend solely on cart actions
  function userInteraction() {
    document.removeEventListener("click", userInteraction, true);
    document.removeEventListener("keydown", userInteraction, true);
    setTimeout(writeCartAttrs, 100);
  }
  document.addEventListener("click", userInteraction, true);
  document.addEventListener("keydown", userInteraction, true);
})();
